import { ReactNode } from 'react';
import { ShieldExclamationIcon } from '@heroicons/react/24/outline';
import type { Permission } from '../lib/roles';
import { useTeamRole } from '../hooks/useTeamRole';

interface PermissionGateProps {
  teamId: string | undefined;
  permission: Permission;
  children: ReactNode;
  /** What to render when permission is denied. Defaults to nothing. */
  fallback?: ReactNode;
  /** 'hide' renders nothing, 'message' shows a denied message */
  mode?: 'hide' | 'message';
}

/**
 * Conditionally renders children based on the user's team role permission.
 * Similar to FeatureGate but for RBAC permissions instead of subscription features.
 */
export default function PermissionGate({
  teamId,
  permission,
  children,
  fallback,
  mode = 'hide',
}: PermissionGateProps) {
  const { hasPermission, isLoading } = useTeamRole(teamId);

  if (isLoading) return null;

  if (hasPermission(permission)) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  if (mode === 'message') {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500">
        <ShieldExclamationIcon className="h-5 w-5 text-gray-400" />
        You don't have permission to access this feature. Contact your team owner to update your role.
      </div>
    );
  }

  return null;
}
